import { ReactElement, useState, useCallback } from 'react';
import { message } from 'antd';
import { ContentBlock, ContentInner } from './Content.style';
import FileUpload from './content/FileUpload';
import RecordingForm from './content/RecordingForm';
import { FormValues } from '../types';
import useUploadRecordings from '../hooks/useUploadRecordings';
import extractTitle from '../utils/extractTitle';

const Content = (): ReactElement => {
  const [messageApi, contextHolder] = message.useMessage();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const { customers, advisors, createRecording, deleteRecording, refreshRecordings } = useUploadRecordings();

  const advisorOptions = advisors.map(a => ({ value: a.id, label: a.name }));
  const customerOptions = customers.map(c => ({ value: c.id, label: c.name }));

  const handleFileSelect = useCallback((file: File | null) => {
    setSelectedFile(file);
  }, []);

  const handleSubmit = useCallback(
    async (values: FormValues) => {
      if (!selectedFile) {
        return;
      }

      setLoading(true);
      try {
        const meetingDateTime = values.meetingDate
          .hour(values.meetingTime.hour())
          .minute(values.meetingTime.minute())
          .second(0)
          .toDate();

        const result = await createRecording({
          title: extractTitle(selectedFile.name),
          customerId: values.customerId,
          advisorId: values.advisorId,
          meetingDateTime,
        });

        // S3にファイルをアップロード
        const uploadResponse = await fetch(result.presignedUrl, {
          method: 'PUT',
          body: selectedFile,
          headers: {
            'Content-Type': 'audio/mp4',
          },
        });

        if (!uploadResponse.ok) {
          // S3アップロード失敗の詳細をログ出力
          const { status } = uploadResponse;
          if (status === 403) {
            console.error('S3 upload failed: Access denied (403). Presigned URL may be expired or invalid.');
          } else if (status === 404) {
            console.error('S3 upload failed: Bucket not found (404).');
          } else if (status === 500) {
            console.error('S3 upload failed: Internal server error (500).');
          } else if (status === 503) {
            console.error('S3 upload failed: Service unavailable (503). Rate limit exceeded or S3 temporarily down.');
          } else {
            console.error('S3 upload failed:', status, uploadResponse.statusText);
          }

          // DBレコードを削除
          try {
            await deleteRecording(result.recordingId);
          } catch (deleteError) {
            console.error('Failed to cleanup recording:', result.recordingId, deleteError);
          }
          messageApi.error('ファイルのアップロードに失敗しました');
          return;
        }

        refreshRecordings();
        messageApi.success('録音をアップロードしました');
      } catch (error) {
        if (error instanceof TypeError && error.message === 'Failed to fetch') {
          console.error('Network error during upload: CORS or connectivity issue.', error);
        } else if (error instanceof Error) {
          console.error('Failed to create recording:', error.name, error.message);
        } else {
          console.error('Failed to create recording:', error);
        }
        messageApi.error('録音の作成に失敗しました');
      } finally {
        setLoading(false);
      }
    },
    [selectedFile, createRecording, deleteRecording, messageApi, refreshRecordings]
  );

  return (
    <ContentBlock>
      {contextHolder}
      <ContentInner>
        <FileUpload onFileSelect={handleFileSelect} selectedFile={selectedFile} />
        <RecordingForm
          advisorOptions={advisorOptions}
          customerOptions={customerOptions}
          selectedFile={selectedFile}
          onSubmit={handleSubmit}
          loading={loading}
        />
      </ContentInner>
    </ContentBlock>
  );
};

export default Content;
